import { VOUCHER_TRANSACTION_PAGES } from "./voucherTransactionPages";

/**
 * Fixed page serial numbers (SL) for ERP screens — matched in order; first win.
 * Paths are normalized to lowercase, no trailing slash (except "/").
 */

function normalizePathname(pathname) {
  let p = String(pathname || "/").split("?")[0].trim().toLowerCase() || "/";
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p;
}

/**
 * @typedef {{ serial: number, name: string, test: (p: string) => boolean }} PageSerialRule
 */

const VOUCHER_PAGE_SERIAL_RULES = VOUCHER_TRANSACTION_PAGES.filter((p) =>
  p.path.startsWith("/voucher/")
).map((p, idx) => ({
  serial: 151 + idx,
  name: p.label,
  test: (path) => path === p.path,
}));

/** @type {PageSerialRule[]} */
const PAGE_SERIAL_RULES = [
  { serial: 1, name: "Login", test: (p) => p === "/login" || p === "/" },
  { serial: 2, name: "Employee portal", test: (p) => p === "/employee-portal" },
  { serial: 3, name: "Change password", test: (p) => p === "/change-password" },
  { serial: 4, name: "Dashboard", test: (p) => p === "/dashboard" },
  { serial: 5, name: "Outward Supply", test: (p) => p === "/billing" },
  { serial: 6, name: "Print bill", test: (p) => p === "/print-bill" },
  { serial: 7, name: "Credit — bill edit", test: (p) => p.startsWith("/credit-due/bill/") && p.endsWith("/edit") },
  {
    serial: 8,
    name: "Credit — customer timeline",
    test: (p) => /^\/credit-due\/customer\/[^/]+\/timeline$/.test(p),
  },
  { serial: 9, name: "Credit — customer", test: (p) => p.startsWith("/credit-due/customer/") },
  { serial: 10, name: "Credit — due over 15", test: (p) => p === "/credit-due/due-over-15" },
  { serial: 11, name: "Credit — receive payment", test: (p) => p === "/credit-due/receive" },
  { serial: 12, name: "Credit — add sale", test: (p) => p === "/credit-due/add" },
  { serial: 13, name: "Credit — payments", test: (p) => p === "/credit-due/payments" },
  { serial: 14, name: "Credit / Due hub", test: (p) => p === "/credit-due" },
  { serial: 15, name: "Inward Supply", test: (p) => p === "/purchase" || p === "/voucher/inward-supply" },
  ...VOUCHER_PAGE_SERIAL_RULES,
  { serial: 16, name: "Party creation", test: (p) => p === "/party-creation" },
  { serial: 17, name: "Ledger (party)", test: (p) => p.startsWith("/ledger/") },
  { serial: 18, name: "Party order history", test: (p) => p.startsWith("/party-order-history/") },
  { serial: 19, name: "Order reports", test: (p) => p === "/reports/order-reports" },
  {
    serial: 20,
    name: "Customer entry — detail",
    test: (p) => p.startsWith("/reports/customer-entry/detail/"),
  },
  { serial: 21, name: "Customer entry — monthly", test: (p) => p === "/reports/customer-entry/monthly" },
  { serial: 22, name: "Customer entry — gifts", test: (p) => p === "/reports/customer-entry/gifts" },
  { serial: 23, name: "Customer entry", test: (p) => p === "/reports/customer-entry" },
  {
    serial: 24,
    name: "Monthly sale report (import)",
    test: (p) => p === "/reports/monthly-sale-report" || p === "/reports/monthly-sale-report-import",
  },
  {
    serial: 2401,
    name: "Monthly import — generate totals",
    test: (p) =>
      p === "/reports/monthly-sale-report-import/generate" || p === "/reports/monthly-sale-report/upload",
  },
  {
    serial: 242,
    name: "Expense upload",
    test: (p) => p === "/reports/expense-upload",
  },
  {
    serial: 243,
    name: "Expense party report",
    test: (p) => p === "/reports/expense-party-monthly",
  },
  {
    serial: 246,
    name: "Employee group monthly sales",
    test: (p) => p === "/reports/party-brand-monthly",
  },
  {
    serial: 244,
    name: "Expense transfer report",
    test: (p) => p === "/reports/expense-transfer-monthly",
  },
  {
    serial: 245,
    name: "Expense Spare Way report",
    test: (p) => p === "/reports/expense-spareway-monthly",
  },
  { serial: 25, name: "Yearly sale report", test: (p) => p === "/reports/yearly-sale-report" },
  { serial: 26, name: "Monthly incentive", test: (p) => p === "/reports/monthly-incentive" },
  {
    serial: 261,
    name: "Monthly incentive — saved",
    test: (p) => p === "/reports/monthly-incentive/view",
  },
  { serial: 27, name: "Reports", test: (p) => p.startsWith("/reports") },
  { serial: 28, name: "Admin panel", test: (p) => p.startsWith("/admin") },
  { serial: 29, name: "Admin dashboard", test: (p) => p === "/admin-dashboard" },
  { serial: 30, name: "Party ageing", test: (p) => p === "/party-ageing-report" },
  { serial: 31, name: "Monthly product report", test: (p) => p === "/monthly-product-report" },
  { serial: 32, name: "Add product", test: (p) => p === "/add-product" },
  { serial: 33, name: "Stock report", test: (p) => p === "/stock-report" },
  { serial: 34, name: "Movement analysis", test: (p) => p === "/movement-analysis" },
  { serial: 35, name: "Stock forecast", test: (p) => p === "/stock-forecast" },
  { serial: 35, name: "Group master", test: (p) => p === "/group-master" },
  {
    serial: 351,
    name: "Party discount / commission",
    test: (p) => p === "/group-commission-settings",
  },
  { serial: 36, name: "Brand master", test: (p) => p === "/brand-master" },
  { serial: 37, name: "Unit master", test: (p) => p === "/unit-master" },
  { serial: 38, name: "GST master", test: (p) => p === "/gst-master" },
  { serial: 39, name: "HSN master", test: (p) => p === "/hsn-master" },
  { serial: 40, name: "Credit / Due master", test: (p) => p === "/credit-due-master" },
  { serial: 41, name: "Party discounts", test: (p) => p === "/party-discounts" },
  { serial: 42, name: "Party discount amounts", test: (p) => p === "/party-discount-amounts" },
  { serial: 43, name: "Profit & loss", test: (p) => p === "/profit-loss" },
  { serial: 44, name: "Cash book", test: (p) => p === "/cash-book" },
  { serial: 45, name: "Bank book", test: (p) => p === "/bank-book" },
  { serial: 46, name: "Excel import", test: (p) => p === "/excel-import" },
  { serial: 47, name: "GSTR-1", test: (p) => p === "/gstr1" },
  { serial: 48, name: "Purchase orders", test: (p) => p === "/purchase-orders" },
  { serial: 49, name: "Pending orders", test: (p) => p === "/pending-orders" },
  { serial: 49, name: "Payroll — attendance audit", test: (p) => p === "/payroll/reports/attendance-audit" },
  { serial: 51, name: "Payroll — branch comparison", test: (p) => p === "/payroll/reports/branch-comparison" },
  { serial: 52, name: "Payroll — shift adherence", test: (p) => p === "/payroll/reports/shift-adherence" },
  { serial: 53, name: "Payroll — OT summary", test: (p) => p === "/payroll/reports/ot-summary" },
  { serial: 54, name: "Payroll — period attendance", test: (p) => p === "/payroll/reports/period-attendance" },
  {
    serial: 541,
    name: "Payroll — monthly status report (four punch)",
    test: (p) => p === "/payroll/reports/employee-attendance-card",
  },
  { serial: 58, name: "Payroll — tenure", test: (p) => p === "/payroll/reports/tenure" },
  { serial: 62, name: "Payroll — employee directory", test: (p) => p === "/payroll/reports/employee-directory" },
  { serial: 63, name: "Payroll — reports hub", test: (p) => p === "/payroll/reports" },
  { serial: 64, name: "Payroll — daily hours", test: (p) => p === "/payroll/daily-hours" },
  { serial: 65, name: "Payroll — holidays", test: (p) => p === "/payroll/holidays" },
  { serial: 66, name: "Payroll — daily late", test: (p) => p === "/payroll/daily-late" },
  { serial: 67, name: "Payroll — yearly attendance", test: (p) => p === "/payroll/yearly-attendance" },
  { serial: 671, name: "Payroll — punch photos", test: (p) => p === "/payroll/attendance-images" },
  { serial: 68, name: "Payroll — employees", test: (p) => p === "/payroll/employees" },
  { serial: 69, name: "Payroll — monthly absent report", test: (p) => p === "/payroll/monthly-report" },
  { serial: 70, name: "Payroll — monitor", test: (p) => p === "/payroll" },
  { serial: 71, name: "Pending order email settings", test: (p) => p === "/tools/pending-order-email" },
  { serial: 72, name: "CCTV live", test: (p) => p === "/cctv" },
  { serial: 73, name: "CCTV cameras", test: (p) => p === "/cctv/cameras" },
  { serial: 74, name: "CCTV settings", test: (p) => p === "/tools/cctv-settings" },
  { serial: 75, name: "Sales import — center", test: (p) => p === "/sales-data-import" },
  { serial: 76, name: "Sales import — branch monthly", test: (p) => p === "/sales-data-import/branch-monthly" },
  { serial: 77, name: "Sales import — brand by branch", test: (p) => p === "/sales-data-import/brand-by-branch" },
  { serial: 78, name: "Sales import — group by branch", test: (p) => p === "/sales-data-import/group-by-branch" },
  {
    serial: 91,
    name: "Sales import — branch voucher reports",
    test: (p) => p === "/sales-data-import/branch-voucher-reports",
  },
  {
    serial: 92,
    name: "Sales import — daily items on sale",
    test: (p) => p === "/sales-data-import/items-on-sale",
  },
  { serial: 79, name: "BI Dashboard", test: (p) => p === "/bi" },
  { serial: 90, name: "BI Business Health Score", test: (p) => p === "/bi/business-health-score" },
  { serial: 80, name: "BI Executive Dashboard", test: (p) => p === "/bi/executive" },
  { serial: 81, name: "BI Slow Moving", test: (p) => p === "/bi/slow-moving" },
  { serial: 82, name: "BI Fast Moving", test: (p) => p === "/bi/fast-moving" },
  { serial: 83, name: "BI Stock Forecast", test: (p) => p === "/bi/forecast" },
  { serial: 84, name: "BI Dead Stock", test: (p) => p === "/bi/dead-stock" },
  { serial: 85, name: "BI Target vs Achievement", test: (p) => p === "/bi/target" },
  { serial: 86, name: "BI Brand Growth", test: (p) => p === "/bi/brand-growth" },
  { serial: 87, name: "BI Employee Performance", test: (p) => p === "/bi/employee" },
  { serial: 88, name: "BI Profit Analysis", test: (p) => p === "/bi/profit" },
  { serial: 89, name: "BI AI Insights", test: (p) => p === "/bi/insights" },
  { serial: 92, name: "Personal data", test: (p) => p === "/personal" },
];

const FALLBACK = { serial: 999, name: "Screen" };

export function getPageSerialInfo(pathname) {
  const p = normalizePathname(pathname);
  for (const r of PAGE_SERIAL_RULES) {
    if (r.test(p)) {
      return {
        serial: r.serial,
        padded: String(r.serial).padStart(3, "0"),
        name: r.name,
      };
    }
  }
  return {
    serial: FALLBACK.serial,
    padded: String(FALLBACK.serial).padStart(3, "0"),
    name: FALLBACK.name,
  };
}
